import { SimpleGrid } from "@chakra-ui/layout"
import * as React from "react"
import styled, { css, FlattenSimpleInterpolation } from "styled-components"

import { Color, LineHeight, SpacingSize, TextSize, Transition } from "~/consts/style-mixins"

export interface Props<StringOrNumber extends string | number = string> {
  values: Array<StringOrNumber> | undefined
  type?: TypeStyle
  columnNumber?: number
  items: {
    text: string
    value: StringOrNumber
    disabled?: boolean
  }[]
  isError?: boolean
  onClick: (items: StringOrNumber[]) => void
  /** これを指定した時、onClickのitemsの要素数が1以下になる */
  noMultiple?: boolean
  checkedBackground?: string
  borderColor?: string
}

// NOTE: valuesの中身とitems.valueの中身はstringとnumberが混在しているため、曖昧比較演算子で比較しなければならない
const Checkbox = <StringOrNumber extends string | number>(props: Props<StringOrNumber>) => {
  const {
    values,
    type = "Medium",
    items,
    onClick,
    isError,
    columnNumber = 1,
    noMultiple,
    checkedBackground = Color.BackgroundGray,
    borderColor,
  } = props
  return (
    <SimpleGrid columns={columnNumber} spacing={SpacingSize.XS} userSelect="none">
      {items.map((item, i) => {
        const isChecked = !!values?.find((v) => v == item.value)
        return (
          <CheckItem
            key={`check_item_${i}_${item.value}`}
            type={type}
            isChecked={isChecked}
            isError={isError}
            borderColor={borderColor}
            disabled={item.disabled}
            text={item.text}
            checkedBackground={checkedBackground}
            onClick={() => {
              if (noMultiple) {
                if (!isChecked) {
                  onClick([item.value])
                } else {
                  onClick([])
                }
                return
              }
              if (item.disabled) {
                return
              }
              let nextValues = Array.from(values ?? [])
              if (isChecked) {
                nextValues = nextValues.filter((v) => v != item.value)
              } else {
                nextValues.push(item.value)
              }
              onClick(nextValues)
            }}
          />
        )
      })}
    </SimpleGrid>
  )
}

export default Checkbox

interface CheckItemProps {
  isChecked: boolean
  isError?: boolean
  disabled?: boolean
  text: string
  checkedBackground?: string
  borderColor?: string
  onClick: () => void
  type: TypeStyle
}

export const CheckItem = ({ type, text, ...props }: CheckItemProps) => (
  <StyledCheckItem {...props} typeStyle={TypeStyle[type]}>
    <div className="circle_outer">
      <div className="check_rect_1" />
      <div className="check_rect_2" />
    </div>
    <div className="text">{text}</div>
  </StyledCheckItem>
)

type TypeStyle = keyof typeof TypeStyle
const TypeStyle = {
  Small: css`
    padding: ${SpacingSize.XS}px;
    font-size: ${TextSize.S}px;
  `,
  Medium: css`
    padding: ${SpacingSize.S}px;
    font-size: ${TextSize.M}px;
  `,
}

type StyledCheckItemProps = Pick<
  CheckItemProps,
  "isChecked" | "isError" | "disabled" | "checkedBackground" | "borderColor"
> & {
  typeStyle: FlattenSimpleInterpolation
}

const StyledCheckItem = styled.div<StyledCheckItemProps>`
  background: ${({ isChecked, isError, disabled, checkedBackground }) =>
    disabled
      ? Color.BackgroundGray
      : isError
      ? Color.BackgroundAttention
      : isChecked
      ? checkedBackground ?? Color.BackgroundGray
      : Color.White};
  border: 1px solid
    ${({ isChecked, isError, disabled, borderColor }) =>
      borderColor
        ? borderColor
        : disabled
        ? Color.BorderGray
        : isError
        ? Color.Attention
        : isChecked
        ? Color.Primary
        : Color.BorderGray};
  box-sizing: border-box;
  border-radius: 4px;
  display: flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  ${Transition.Hover};
  &:hover {
    border: 1px solid
      ${({ isChecked, disabled, borderColor }) =>
        borderColor ? borderColor : disabled ? Color.BorderGray : isChecked ? Color.Primary : Color.Gray};
    .circle_outer {
      border: 1px solid
        ${({ isChecked, disabled }) => (disabled ? Color.LightGray : isChecked ? Color.Primary : Color.Gray)};
    }
  }
  .circle_outer {
    position: relative;
    width: 20px;
    height: 20px;
    border-radius: 2px;
    background: ${({ isChecked, disabled }) =>
      isChecked && !disabled ? Color.Primary : isChecked && disabled ? Color.Gray : Color.White};
    border: 1px solid
      ${({ isChecked, disabled }) =>
        isChecked && !disabled
          ? Color.Primary
          : isChecked && disabled
          ? Color.Gray
          : disabled
          ? Color.LightGray
          : Color.Black};
    box-sizing: border-box;
    transition: 0.2s;
  }
  .check_rect_1 {
    position: absolute;
    position: absolute;
    width: 9px;
    height: 0px;
    left: 7px;
    top: 8px;
    border: 1px solid ${Color.White};
    transform: rotate(135deg);
    background: ${({ isChecked, disabled }) => (isChecked && !disabled ? Color.Primary : Color.White)};
  }
  .check_rect_2 {
    position: absolute;
    width: 6px;
    height: 0px;
    left: 4px;
    top: 10px;
    border: 1px solid ${Color.White};
    transform: rotate(45deg);
    background: ${({ isChecked, disabled }) => (isChecked && !disabled ? Color.Primary : Color.White)};
  }
  .text {
    width: calc(100% - 20px);
    padding-left: ${SpacingSize.XS}px;
    color: ${({ disabled }) => (disabled ? Color.LightGray : Color.Black)};
    line-height: ${LineHeight.S}%;
  }
  ${({ typeStyle }) => typeStyle}
`
